/*
  ChartsContext.tsx

  The context keeps track of the charts, and their configuration. It also provides
  a way to add, remove, and update charts.
*/
import React, {createContext, Dispatch, ReactNode, useContext} from 'react';
import {v4 as uuidv4} from 'uuid';

import {ChartConfig} from '../pages/wfReactInterface/generatedBuiltinObjectClasses.zod';
import {chartAxisFields} from './extractData';
import {loadChartsConfig, saveChartsConfig} from './persistence';
import {ChartsAction, ChartsState, InternalChartConfig} from './types';

const DEFAULT_BIN_COUNT = 20;
const defaultXAxis = chartAxisFields[0]?.key || 'started_at';
const defaultYAxis =
  chartAxisFields.find(f => f.type === 'number')?.key || 'latency';

// Convert ChartConfig to InternalChartConfig by adding id
const chartConfigToInternal = (config: ChartConfig): InternalChartConfig => ({
  ...config,
  id: uuidv4(),
});

// Convert InternalChartConfig to ChartConfig by removing id
const internalToChartConfig = (internal: InternalChartConfig): ChartConfig => {
  const {id, ...chartConfig} = internal;
  return chartConfig;
};

// Simple mapping on load; edits are applied by index while modal is open
const chartsToInternal = (incoming: ChartConfig[]): InternalChartConfig[] =>
  incoming.map(chartConfigToInternal);

export function chartsReducer(
  state: ChartsState,
  action: ChartsAction
): ChartsState {
  switch (action.type) {
    case 'SET_CHARTS': {
      return {
        ...state,
        charts: action.payload,
      };
    }
    case 'ADD_CHART': {
      const needsBinning =
        action.payload?.plot_type === 'line' ||
        action.payload?.plot_type === 'bar';
      const newChart: InternalChartConfig = {
        id: uuidv4(),
        x_axis: action.payload?.x_axis || defaultXAxis,
        y_axis: action.payload?.y_axis || defaultYAxis,
        plot_type: action.payload?.plot_type || 'scatter',
        ...(needsBinning
          ? {bin_count: DEFAULT_BIN_COUNT, aggregation: 'average'}
          : {}),
      };
      return {
        ...state,
        charts: [...state.charts, newChart],
      };
    }
    case 'REMOVE_CHART': {
      const newCharts = state.charts.filter(c => c.id !== action.id);
      const openDrawerChartId =
        state.openDrawerChartId === action.id ? null : state.openDrawerChartId;
      return {
        ...state,
        charts: newCharts,
        openDrawerChartId,
      };
    }
    case 'UPDATE_CHART': {
      return {
        ...state,
        charts: state.charts.map(c =>
          c.id === action.id ? {...c, ...action.payload} : c
        ),
      };
    }
    case 'UPDATE_CHART_BY_INDEX': {
      const {index, payload} = action;
      if (index < 0 || index >= state.charts.length) {
        return state;
      }
      const newCharts = state.charts.slice();
      newCharts[index] = {...newCharts[index], ...payload};
      return {
        ...state,
        charts: newCharts,
      };
    }
    case 'OPEN_DRAWER': {
      return {
        ...state,
        openDrawerChartId: action.id,
      };
    }
    case 'CLOSE_DRAWER': {
      return {
        ...state,
        openDrawerChartId: null,
      };
    }
    case 'REORDER_CHARTS': {
      const {sourceIndex, destinationIndex} = action;
      const newCharts = Array.from(state.charts);
      const [removed] = newCharts.splice(sourceIndex, 1);
      newCharts.splice(destinationIndex, 0, removed);
      return {
        ...state,
        charts: newCharts,
      };
    }

    default:
      return state;
  }
}

const ChartsStateContext = createContext<ChartsState | undefined>(undefined);
const ChartsDispatchContext = createContext<Dispatch<ChartsAction> | undefined>(
  undefined
);

export const ChartsProvider = ({
  children,
  entity,
  project,
  charts,
  setCharts,
  currentViewId,
}: {
  children: ReactNode;
  entity: string;
  project: string;
  charts?: ChartConfig[] | null;
  setCharts?: (newCharts: ChartConfig[] | null) => void;
  currentViewId?: string;
}) => {
  const [state, dispatch] = React.useReducer(chartsReducer, {
    charts: [],
    openDrawerChartId: null,
  });

  // Track whether user has made changes and initial charts state
  const hasUserModifiedCharts = React.useRef(false);
  const initialChartsJson = React.useRef<string | null>(null);

  // Wrap dispatch to track user modifications
  const trackedDispatch = React.useCallback((action: ChartsAction) => {
    // SET_CHARTS is used for loading, not user actions
    if (action.type !== 'SET_CHARTS') {
      hasUserModifiedCharts.current = true;
    }
    dispatch(action);
  }, []);

  // Track the previous charts from props to detect changes
  const prevChartsRef = React.useRef<ChartConfig[] | null | undefined>();
  const prevViewIdRef = React.useRef<string | undefined>();

  // Determine if we should use saved view charts or localStorage
  const isUsingDefaultView =
    !currentViewId ||
    currentViewId === 'traces_default' ||
    currentViewId === 'evaluations_default';

  // Load charts when view changes or on mount
  React.useEffect(() => {
    const loadCharts = async () => {
      // If view changed, reload charts
      if (prevViewIdRef.current !== currentViewId) {
        prevViewIdRef.current = currentViewId;
        hasUserModifiedCharts.current = false; // Reset on view change
        initialChartsJson.current = null; // Reset initial charts

        if (!isUsingDefaultView && charts) {
          const internalCharts = chartsToInternal(charts);
          dispatch({type: 'SET_CHARTS', payload: internalCharts});
          prevChartsRef.current = charts;
          // Store initial charts for comparison
          initialChartsJson.current = JSON.stringify(charts);
        } else {
          // Load from localStorage for default views
          const loadedCharts = await loadChartsConfig(entity, project);
          dispatch({type: 'SET_CHARTS', payload: loadedCharts});
          // For default views, store the loaded charts as initial
          const loadedAsConfigs = loadedCharts.map(internalToChartConfig);
          initialChartsJson.current = JSON.stringify(loadedAsConfigs);
        }
      }
      // If charts prop changed while on the same view, update
      else if (!isUsingDefaultView && charts !== prevChartsRef.current) {
        if (charts) {
          const internalCharts = chartsToInternal(charts);
          dispatch({type: 'SET_CHARTS', payload: internalCharts});
          // Reset the modification flag and update initial charts when charts prop changes
          hasUserModifiedCharts.current = false;
          initialChartsJson.current = JSON.stringify(charts);
        }
        prevChartsRef.current = charts;
      }
    };
    loadCharts();
  }, [entity, project, charts, currentViewId, isUsingDefaultView]);

  // Save charts back - but only when they actually change due to user interaction
  React.useEffect(() => {
    // Skip saves if user hasn't modified charts
    if (!hasUserModifiedCharts.current) {
      return;
    }

    if (setCharts && !isUsingDefaultView) {
      // Sync with parent state for saved views
      // Convert InternalChartConfig[] to ChartConfig[]
      const chartConfigs = state.charts.map(internalToChartConfig);

      // Only call setCharts if the charts are actually different from initial
      const currentChartsJson = JSON.stringify(chartConfigs);
      if (currentChartsJson !== initialChartsJson.current) {
        setCharts(chartConfigs);
      }
    } else {
      // Save to localStorage for default views
      const saveCharts = async () => {
        await saveChartsConfig(state.charts, entity, project);
      };
      saveCharts();
    }
  }, [state.charts, entity, project, setCharts, isUsingDefaultView]);

  return (
    <ChartsStateContext.Provider value={state}>
      <ChartsDispatchContext.Provider value={trackedDispatch}>
        {children}
      </ChartsDispatchContext.Provider>
    </ChartsStateContext.Provider>
  );
};

export function useChartsState() {
  const ctx = useContext(ChartsStateContext);
  if (!ctx)
    throw new Error('useChartsState must be used within ChartsProvider');
  return ctx;
}

export function useChartsDispatch() {
  const ctx = useContext(ChartsDispatchContext);
  if (!ctx)
    throw new Error('useChartsDispatch must be used within ChartsProvider');
  return ctx;
}
